"use client"

import { Button, Card, Row, Col, Typography, Layout, Badge } from "antd"
import { Navbar, Nav, Container, Offcanvas } from "react-bootstrap"
import { FaBalanceScale, FaEye, FaChartBar, FaRegHeart, FaRegStar, FaBars } from "react-icons/fa"
import { FaArrowRightLong } from "react-icons/fa6";
import { SlEnergy } from "react-icons/sl"
import { AiOutlineEye } from "react-icons/ai";
import { BsStars } from "react-icons/bs"
import { type ReactNode, useState } from "react"
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo/akakshik.svg";
import "./home.scss";
import { getLocalStorageData } from "../../utils/LocalStorageService";

// import "./home.scss"

const { Title, Paragraph, Text } = Typography
const { Content, Footer } = Layout

interface Feature {
    icon: ReactNode
    title: string
    description: string
}

export default function AkakshiLanding() {
    const [showSidebar, setShowSidebar] = useState(false)
    const navigate = useNavigate();
    const [activeLink, setActiveLink] = useState("");
    const handleCloseSidebar = () => setShowSidebar(false)
    const handleShowSidebar = () => setShowSidebar(true)

    const localUserObj: any = getLocalStorageData("localUser");

    const handleSignIn = (link: string) => {
        setActiveLink(link);
        navigate("/login");
    };

    const features: Feature[] = [
        {
            icon: <FaBalanceScale className="icon-size" />,
            title: "Karmic Balance Tracking",
            description: "Track your karmic balance in real-time with our advanced spiritual accounting system.",
        },
        {
            icon: <FaRegHeart className="icon-size" />,
            title: "Intention Logging",
            description: "Record and monitor the intentions behind all your actions for deeper self-awareness.",
        },
        {
            icon: <FaChartBar className="icon-size" />,
            title: "Cosmic Analytics",
            description: "Visualize your spiritual progress with comprehensive analytics and insights.",
        },
        {
            icon: <SlEnergy className="icon-size" />,
            title: "Energy Alignment",
            description: "Receive personalized recommendations to align your energy with cosmic rhythms.",
        },
        {
            icon: <FaRegStar className="icon-size" />,
            title: "Spiritual Milestones",
            description: "Celebrate your spiritual growth with meaningful milestones and achievements.",
        },
        {
            icon: <FaEye className="icon-size" />,
            title: "Akashic Insights",
            description: "Access deeper wisdom and insights from your personal Akashic records.",
        },
    ]

    const steps = [
        {
            number: "1",
            title: "Record Actions",
            description: "Log your daily actions, thoughts, and intentions with our intuitive interface.",
        },
        {
            number: "2",
            title: "Analyze Patterns",
            description: "Our AI analyzes your karma patterns and provides insights into your spiritual journey.",
        },
        {
            number: "3",
            title: "Balance & Grow",
            description: "Receive guidance to balance your spiritual and material growth.",
        },
    ]

    const handleNavClick = (link: string) => {
        setActiveLink(link);
        handleCloseSidebar();
    };

    return (
        <Layout className="min-vh-100">
            {/* Header using React Bootstrap */}
            <Navbar bg="white" expand="lg" className="shadow-sm sticky-top" style={{ zIndex: 1000 }}>
                <Container>
                    <Navbar.Brand href="#" className="d-flex align-items-center">
                        <img src={logo} alt="Akakshi Logo" style={{ height: 75, width: 75 }} />
                    </Navbar.Brand>

                    <div className="d-flex">
                        {/* Desktop Menu */}
                        <Nav className="mx-auto d-none d-lg-flex align-items-center">
                            <Nav.Link href="#features" className="mx-2">
                                Features
                            </Nav.Link>
                            <Nav.Link href="#how-it-works" className="mx-2">
                                How it Works
                            </Nav.Link>
                            <Nav.Link href="#pricing" className="mx-2">
                                Pricing
                            </Nav.Link>
                            {localUserObj?.token ? (
                                <Nav.Link href="/dashboard" className="mx-2">
                                    Dashboard
                                </Nav.Link>
                            ) : (
                                <Button onClick={() => {
                                    handleSignIn("signin");
                                }} type="default" className="btn-outline-accent d-none d-lg-inline-flex me-3">
                                    Sign In
                                </Button>
                            )}
                        </Nav>
                        <div className="d-flex align-items-center">
                            <Button type="primary" className="btn-accent d-none d-lg-inline-flex me-3">
                                Get Started
                            </Button>

                            {/* Mobile Hamburger Menu */}
                            <Button
                                type="text"
                                className="d-lg-none text-accent"
                                onClick={handleShowSidebar}
                                icon={<FaBars />}
                                style={{ border: "none" }}
                            />
                        </div>
                    </div>
                </Container>
            </Navbar>
            <Offcanvas show={showSidebar} onHide={handleCloseSidebar} placement="end">
                <Offcanvas.Header className="border-bottom py-2 px-3 bg-white justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <img src={logo} alt="Akakshi Logo" style={{ height: 60, width: 60 }} />
                    </div>
                    <button
                        type="button"
                        className="custom-close-icon"
                        onClick={handleCloseSidebar}
                        aria-label="Close"
                    ></button>
                </Offcanvas.Header>

                <Offcanvas.Body>
                    <Nav className="flex-column">
                        <Nav.Link
                            href="#features"
                            onClick={() => handleNavClick("features")}
                            className={`py-3 ${activeLink === "features" ? "active-link" : "text-dark"}`}
                        >
                            Features
                        </Nav.Link>

                        <Nav.Link
                            href="#how-it-works"
                            onClick={() => handleNavClick("how-it-works")}
                            className={`py-3 ${activeLink === "how-it-works" ? "active-link" : "text-dark"}`}
                        >
                            How it Works
                        </Nav.Link>

                        <Nav.Link
                            href="#pricing"
                            onClick={() => handleNavClick("pricing")}
                            className={`py-3 ${activeLink === "pricing" ? "active-link" : "text-dark"}`}
                        >
                            Pricing
                        </Nav.Link>

                        <Nav.Link
                            href="#signin"
                            onClick={() => handleSignIn("signin")}
                            className={`py-3 ${activeLink === "signin" ? "active-link" : "text-dark"}`}
                        >
                            Sign In
                        </Nav.Link>

                        <div className="mt-4">
                            <Button type="primary" className="btn-accent w-100" onClick={handleCloseSidebar}>
                                Get Started
                            </Button>
                        </div>
                    </Nav>
                </Offcanvas.Body>
            </Offcanvas>

            <Content>
                {/* Hero Section */}
                <section className="py-5 bg-hero min-vh-80">
                    <Container>
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10 text-center py-3 py-md-5">
                                {/* Notification Badge */}
                                <div className="d-flex justify-content-center align-items-center">
                                    <BsStars className="me-2 text-accent" />
                                    <Title level={5} className="text-accent fs-6">
                                        Track Your Karmic Journey
                                    </Title>
                                </div>

                                <div className="mt-2 mt-md-4">
                                    <Title level={1} className="display-heading mb-2 mb-md-3 text-accent">
                                        Akakshi
                                    </Title>

                                    <Title level={2} className="display-title mb-3 mb-md-4 text-dark">
                                        Karmic Accounting System
                                    </Title>

                                    <Paragraph className="lead-para mx-auto mb-4 mb-md-5">
                                        Balance your spiritual ledger. Track actions, intentions, and their cosmic consequences with the
                                        world's first karmic accounting platform.
                                    </Paragraph>

                                    <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center align-items-center mb-4 mb-md-5">
                                        <Button
                                            size="middle"
                                            className="btn-accent px-4 py-3 w-100 w-sm-auto"
                                            icon={<FaArrowRightLong className="w-4 h-4" />}
                                            iconPosition="end"
                                        >
                                            Start Your Journey
                                        </Button>
                                        <Button size="small" className="btn-outline-accent px-4 py-3 w-100 w-sm-auto bg-section">
                                            Watch Demo
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* Features Section */}
                <section className="py-5 bg-white" id="features">
                    <Container>
                        <div className="text-center mb-5">
                            <Title level={1} className="mb-3 text-dark">
                                Features That Illuminate Your Path
                            </Title>
                            <Paragraph className="text-muted-custom fs-5 mx-auto" style={{ maxWidth: "600px" }}>
                                Comprehensive tools to track, analyze, and balance your karmic energy across all aspects of life.
                            </Paragraph>
                        </div>

                        <Row gutter={[32, 32]}>
                            {features.map((feature, index) => (
                                <Col xs={24} sm={12} md={8} key={index}>
                                    <Card className="h-100 border-0 shadow-sm text-start" bodyStyle={{ padding: "2rem" }}>
                                        <div className="icon-container mb-3 rounded bg-accent text-white">
                                            {feature.icon}
                                        </div>
                                        <Title level={4} className="mb-3 text-dark">
                                            {feature.title}
                                        </Title>
                                        <Paragraph className="text-muted-custom">{feature.description}</Paragraph>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* How It Works Section */}
                <section className="py-5 bg-section" id="how-it-works">
                    <Container>
                        <div className="text-center mb-5">
                            <Title level={2} className="mb-3 text-dark-custom">
                                How Akakshi Works
                            </Title>
                            <Paragraph className="text-muted-custom fs-5">
                                Simple steps to begin tracking and balancing your karmic energy
                            </Paragraph>
                        </div>

                        <Row gutter={[48, 48]}>
                            {steps.map((step, index) => (
                                <Col xs={24} md={8} key={index} className="text-center">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center mb-4 rounded-circle bg-accent text-white fw-bold"
                                        style={{ width: 60, height: 60, fontSize: "1.5rem" }}
                                    >
                                        {step.number}
                                    </div>

                                    <Title level={4} className="mb-3 text-dark-custom">
                                        {step.title}
                                    </Title>
                                    <Paragraph className="text-muted-custom">{step.description}</Paragraph>
                                </Col>
                            ))}
                        </Row>
                    </Container>
                </section>

                {/* CTA Section */}
                <section className="py-5 bg-accent">
                    <Container>
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10 text-center text-white">
                                <Title level={2} className="display-title mb-3 text-white">
                                    Begin Your Karmic Journey Today
                                </Title>
                                <Paragraph className="lead-para mb-4 text-accent-light mx-auto">
                                    Join thousands of souls who are already tracking and balancing their karmic energy with Akakshi.
                                </Paragraph>

                                <div className="d-flex flex-column flex-md-row gap-3 justify-content-center align-items-center">
                                    <Button
                                        size="large"
                                        className="px-4 py-2 w-100 w-md-auto bg-white text-accent border-white fw-medium"
                                        icon={<FaArrowRightLong className="w-4 h-4" />}
                                        iconPosition="end"
                                    >
                                        Start Free Trial
                                    </Button>

                                    <Button
                                        size="large"
                                        className="px-4 py-2 w-100 w-md-auto btn-outline-accent text-white fw-medium"
                                    >
                                        Learn More
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

            </Content>

            {/* Footer */}
            <Footer className="bg-footer text-white">
                <Container>
                    <Row gutter={[32, 32]}>
                        <Col xs={24} md={6}>
                            <div className="d-flex align-items-center mb-3">
                                <Button
                                    type="primary"
                                    size="small"
                                    className="btn-accent px-2 py-2 me-1"
                                    icon={<AiOutlineEye className="w-4 h-4 text-white" />}
                                />
                                <Text strong className="text-white fs-5">
                                    Akakshi
                                </Text>
                            </div>
                            <Paragraph className="text-footer-grey text-start">
                                Illuminating the path to karmic balance and spiritual growth.
                            </Paragraph>
                        </Col>

                        <Col xs={12} md={6}>
                            <Title level={5} className="text-white text-start mb-3">
                                Product
                            </Title>
                            <div className="d-flex flex-column text-start gap-2">
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Features
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Pricing
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    API
                                </a>
                            </div>
                        </Col>

                        <Col xs={12} md={6}>
                            <Title level={5} className="text-white text-start mb-3">
                                Company
                            </Title>
                            <div className="d-flex flex-column text-start gap-2">
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    About
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Blog
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Careers
                                </a>
                            </div>
                        </Col>

                        <Col xs={24} md={6}>
                            <Title level={5} className="text-white text-start mb-3">
                                Support
                            </Title>
                            <div className="d-flex flex-column text-start gap-2">
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Help Center
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Contact
                                </a>
                                <a href="#" className="text-decoration-none text-footer-grey">
                                    Privacy
                                </a>
                            </div>
                        </Col>
                    </Row>

                    <div className="border-top mt-4 pt-4" style={{ borderColor: "#222833ff" }}>
                        <Text className="text-footer-grey">© 2025 Akakshi. All rights reserved. May your karma be balanced.</Text>
                    </div>
                </Container>
            </Footer>
        </Layout>
    )
}
